/**
 * Loads a library song for the Creator screens and makes sure it lives on a
 * channel of the shared audio engine (so Play / Master / Export / "Open
 * Advanced Studio" all operate on the same audio). Polls cover art while pending.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getEngine } from "@/lib/engineSingleton";
import { getSong, loadUrlIntoChannel, playableUrl } from "@/lib/creatorApi";
import { setCurrentSongId } from "@/lib/studioMode";

export function findSongChannel(engine, song) {
  if (!engine?.channels || !song?.audio_url) return -1;
  return engine.channels.findIndex((c) => c?.buffer && c.sourceUrl === song.audio_url);
}

export default function useCreatorSong(id) {
  const [engine] = useState(() => getEngine());
  const [song, setSong] = useState(null);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState(null);
  const [loadState, setLoadState] = useState("idle"); // idle | loading | ready | error
  const loadingRef = useRef(false);

  const reload = useCallback(async () => {
    if (!id) return null;
    const s = await getSong(id);
    setSong(s);
    return s;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setSong(null); setError(null); setChannel(null); setLoadState("idle");
    if (!id) return undefined;
    getSong(id)
      .then((s) => { if (!cancelled) { setSong(s); setCurrentSongId(s.id); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.status === 404 ? "We couldn't find that song." : (e?.message || "Could not load the song.")); });
    return () => { cancelled = true; };
  }, [id]);

  // Put the song on an engine channel exactly once per song.
  useEffect(() => {
    if (!song?.audio_url || !engine?.channels || loadingRef.current) return;
    const existing = findSongChannel(engine, song);
    if (existing >= 0) { setChannel(existing); setLoadState("ready"); return; }
    loadingRef.current = true;
    setLoadState("loading");
    (async () => {
      try {
        const [idx] = engine.freeChannelIndices(1);
        if (idx == null) throw new Error("The studio is full — clear a channel in the Advanced Studio first.");
        await loadUrlIntoChannel(engine, idx, playableUrl(song.audio_url), `${song.title || "song"}.mp3`, song.audio_url);
        setChannel(idx);
        setLoadState("ready");
      } catch (e) {
        setError(e?.message || "This song's audio could not be loaded.");
        setLoadState("error");
      } finally {
        loadingRef.current = false;
      }
    })();
  }, [song, engine]);

  // Cover art polling while the AI is painting.
  useEffect(() => {
    if (song?.cover_status !== "pending") return undefined;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const fresh = await getSong(song.id);
        if (fresh.cover_status !== "pending" || tries >= 20) { setSong(fresh); clearInterval(t); }
      } catch (e) { void e; }
    }, 3000);
    return () => clearInterval(t);
  }, [song?.cover_status, song?.id]);

  return { engine, song, setSong, error, channel, loadState, reload };
}
